import React from 'react';
import styled from 'styled-components';

import { RequiredIcon } from '@/assets/icons/required';
import { InfoCircleIcon } from '@/assets/static/icons/info-circle';
import { themeVar } from '@/styles/theme';
import { css } from 'styled-components';
import { Tooltip } from '../../basicComponents/tooltip';

interface LabelProps {
  children: React.ReactNode;
  required?: boolean;
  size?: 'sm' | 'md' | 'lg';
  tooltip?: string;
  tooltipWidth?: number;
  className?: string;
  'data-testid'?: string;
}

const getLabelSize = (size: LabelProps['size']) => {
  switch (size) {
    case 'sm':
      return css`
        font-size: 13px;
        line-height: 18.2px;
        padding: 3px;
        gap: 5px;
      `;
    case 'md':
      return css`
        font-size: 16px;
        line-height: 22.4px;
        padding: 4px;
        gap: 6px;
      `;
    case 'lg':
      return css`
        font-size: 19px;
        line-height: 26.6px;
        padding: 5px;
        gap: 8px;
      `;
    default:
      return '';
  }
};

const requiredIconWidths = {
  sm: {
    width: '8px',
    height: '7.7px'
  },
  md: {
    width: '10px',
    height: '9.6px'
  },
  lg: {
    width: '12px',
    height: '11.6px'
  }
};

const Label = ({ children, required = false, size = 'lg', tooltip, tooltipWidth = 0, className, 'data-testid': testId }: LabelProps) => {
  return (
    <LabelContainer data-testid={testId} className={className} size={size}>
      {required && <RequiredIcon width={requiredIconWidths[size].width} height={requiredIconWidths[size].height} fill={themeVar('context.error')} />}
      {children}
      {tooltip ? (
        <Tooltip title={tooltip} maxContentWidth={tooltipWidth}>
          <InfoCircleIcon fill={themeVar('font.disabled')} style={{ position: 'relative', top: '3px' }} />
        </Tooltip>
      ) : (
        <Space />
      )}
    </LabelContainer>
  );
};

export default Label;

const LabelContainer = styled.span<{ size: LabelProps['size'] }>`
  display: flex;
  align-items: center;
  font-family: Blinker;
  font-weight: 400;
  color: ${themeVar('font.base')};

  ${({ size }) => getLabelSize(size)}
`;

const Space = styled.div`
  height: 24.9px;
`;
